import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import { useTheme } from 'styled-components';
import writeXlsxFile, { Cell } from 'write-excel-file';

import { useLocalize } from '@treams/treams-hooks';
import { DownloadIcon } from '@treams/treams-icons';
import {
	AnalyticsEngagementOpenQuestion,
	AnalyticsEngagementQuestionScoreTrend,
	AnalyticsFilters,
	EngagementCategory,
} from '@treams/treams-network';
import { Button, tokens } from '@treams/treams-ui';

type Props = {
	categories: Map<string, EngagementCategory>;
	groups: string[];
	grouped: Record<
		string,
		(AnalyticsEngagementQuestionScoreTrend | AnalyticsEngagementOpenQuestion)[]
	>;
	filters: AnalyticsFilters;
};

export const EngagementQuestionsExportButton: FC<Props> = ({
	filters,
	categories,
	groups,
	grouped,
}) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const engagmentKeyPrefix = 'analytics.engagementQuestions.export';
	const theme = useTheme();

	const getExportName = () => {
		const startDate = moment(filters.startDate).format('DD-MM-YYYY');
		const endDate = moment(filters.endDate).format('DD-MM-YYYY');
		const name = `${t(`${engagmentKeyPrefix}.name`)} ${startDate} - ${endDate}`;

		return name;
	};

	const handleExport = async () => {
		const name = getExportName();
		const rows: Cell[][] = [];

		rows.push([{ value: name, fontSize: 16, fontWeight: 'bold' }]);
		rows.push([{ value: '' }]);

		for (const group of groups) {
			const category = categories.get(group);
			const questions = grouped[group];

			if (!category || !questions) {
				continue;
			}
			rows.push([{ value: localize(category.title).text, fontSize: 14 }]);

			for (const question of questions) {
				rows.push([
					{ value: localize(question.question.title).text, fontSize: 12, fontWeight: 'bold' },
				]);

				if ('datapoints' in question) {
					const scoreAnswers = question.datapoints;
					const latestScore = scoreAnswers[scoreAnswers.length - 1]?.score;
					const rounded = latestScore?.toFixed(1) ?? '-';

					const color =
						question.delta && question.delta > 0
							? theme.colors.green
							: question.delta && question.delta < 0
								? theme.colors.red
								: tokens.text.secondary;

					rows.push([{ value: t(`${engagmentKeyPrefix}.average`) }, { value: rounded }]);

					rows.push([
						{ value: t(`${engagmentKeyPrefix}.difference`) },
						{ value: question.delta?.toFixed(1) ?? '-', color },
					]);
					rows.push([{ value: t(`${engagmentKeyPrefix}.answers`) }]);

					for (const [index, datapoint] of scoreAnswers.entries()) {
						rows.push([
							{
								value: `${index + 1}. `,
								align: 'right',
								alignVertical: 'top',
								color: '#4d4d4d',
								fontSize: 11,
							},
							{ value: datapoint.score?.toFixed(1) ?? '-' },
						]);
					}
				}

				// Handle open answers
				if ('answers' in question) {
					const openAnswers = question.answers;
					rows.push([{ value: t(`${engagmentKeyPrefix}.answers`) }]);

					for (const [index, answer] of openAnswers.entries()) {
						rows.push([
							{
								value: `${index + 1}. `,
								align: 'right',
								alignVertical: 'top',
								color: '#4d4d4d',
								fontSize: 11,
							},
							{ value: answer.text, wrap: true },
						]);
					}
				}

				rows.push([{ value: '' }]);
			}
		}

		await writeXlsxFile(rows, {
			columns: [{ width: 15 }, { width: 80 }, { width: 20 }, { width: 5 }],
			fileName: `${name}.xlsx`,
		});
	};

	return (
		<Flex justifyContent="flex-end">
			<Button
				type="secondary"
				icon={<DownloadIcon />}
				title={t('analytics.metric.downloadButton')}
				onClick={handleExport}
			/>
		</Flex>
	);
};
